"use client";
import React, { useCallback, useEffect, useMemo } from "react";
import { ChatsContext, CurrentChatContext, UserContext } from "@/components/context";
import { IChat, IUser } from "@/lib/types";
import { chatStorage, getUserFromInstance, MAX_CHAT_LIST } from "@/lib/utils";
import { useMsal } from "@azure/msal-react";

export const Providers = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = React.useState<IUser | undefined>(undefined);
    const { accounts } = useMsal();

    const updateUser = useCallback((inputUser: IUser | undefined) => {
        setUser(inputUser);
    }, []);

    const userContextValue = useMemo(
        () => ({
            user: user,
            updateUser: updateUser,
        }),
        [user, updateUser]
    );

    const [chats, setChats] = React.useState<IChat[]>([]);

    const deleteChat = useCallback(
        (id: string) => {
            const deleteChat = async (id: string) => {
                const chatStore = chatStorage();
                await chatStore.init();
                try {
                    await chatStore.deleteChat(id);
                } finally {
                    chatStore.close();
                }
            };
            const newChats = chats.filter((chat) => chat.id !== id);
            setChats(newChats);
            return deleteChat(id);
        },
        [chats]
    );

    const updateChat = useCallback(
        (inputChat: IChat) => {
            const saveChat = async (chat: IChat) => {
                const chatStore = chatStorage();
                await chatStore.init();
                try {
                    await chatStore.writeChat(chat);
                } finally {
                    chatStore.close();
                }
            };
            const existingChat = chats.find((chat) => chat.id === inputChat.id);
            if (existingChat) {
                const newChats = chats.map((chat) => (chat.id === inputChat.id ? inputChat : chat));
                setChats(newChats);
            } else {
                const newChats = [...chats, inputChat].sort((a, b) => b.timestamp - a.timestamp);
                if (newChats.length > MAX_CHAT_LIST) {
                    const elementsToDelete = newChats.slice(MAX_CHAT_LIST);
                    elementsToDelete.forEach((chat) => deleteChat(chat.id));
                    setChats(newChats.slice(0, MAX_CHAT_LIST));
                } else {
                    setChats(newChats);
                }
            }
            return saveChat(inputChat);
        },
        [chats, deleteChat]
    );

    const chatsContextValue = useMemo(
        () => ({
            chats: chats,
            updateChat: updateChat,
            deleteChat: deleteChat,
        }),
        [chats, updateChat, deleteChat]
    );

    // The current chat always starts out as empty
    const [currentChat, setCurrentChat] = React.useState<IChat | undefined>(undefined);

    const updateCurrentChat = useCallback((chat: IChat | undefined) => {
        setCurrentChat(chat);
    }, []);

    const currentChatContextValue = useMemo(
        () => ({
            currentChat: currentChat,
            updateCurrentChat: updateCurrentChat,
        }),
        [currentChat, updateCurrentChat]
    );

    useEffect(() => {
        if (accounts.length === 0) {
            setUser(undefined);
        } else {
            setUser(getUserFromInstance(accounts[0]));
        }
        // Load chats from local storage
        const getChats = async (): Promise<IChat[]> => {
            const chatStore = chatStorage();
            await chatStore.init();
            try {
                return chatStore.readChats();
            } finally {
                chatStore.close();
            }
        };
        getChats().then((chats) => setChats(chats));
    }, [accounts]);

    return (
        <>
            <UserContext.Provider value={userContextValue}>
                <ChatsContext.Provider value={chatsContextValue}>
                    <CurrentChatContext.Provider value={currentChatContextValue}>
                        {children}
                    </CurrentChatContext.Provider>
                </ChatsContext.Provider>
            </UserContext.Provider>
        </>
    );
};
